import { Layer, MapRef, useMapInstance } from 'shared/map-exports';
import {
  MapClickHandler,
  MapboxFeature,
  MapLayerProps,
} from 'features/mapping/mapping.types';
import { addVisible } from 'shared/addVisible';
import { useMemo } from 'react';
import { useSelectedAQISensorId } from 'features/aqiSensors/hooks/useSelectedAQISensorId';
import { LatLng } from 'shared/types';
import { useAQISensor } from 'features/aqiSensors/hooks/useAQISensor';
import { useCenterMap } from 'features/mapping/hooks/useCenterMap';
import { usePulseAnimation } from 'hooks/usePulseAnimation';
import { history } from 'shared/history';
import { useTranslation } from 'react-i18next';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import {
  AQI_BASE_POINT_BLURS_LAYER_ID,
  AQI_LABELS_LAYER_ID,
  AQI_POINT_BLURS_LAYER_ID,
  AQI_POINT_ICONS_LAYER_ID,
  AQI_POINT_ICONS_MODERATE_OR_ABOVE_LAYER_ID,
  AQI_SELECTED_POINT_ICON_LAYER_ID,
  AQI_SELECTED_POINT_ICON_MODERATE_OR_ABOVE_LAYER_ID,
  AQI_SOURCE_ID,
  AQI_SOURCE_LAYER,
  BASE_PULSE_CIRCLE_RADIUS,
  MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
  MIN_ICON_ZOOM_LEVEL,
} from './AQILayer.constants';
import { getWatchdutyTileUrl } from '../layers.utils';
import {
  getBasePointBlurStyle,
  getPointBlurStyle,
  getSelectedPointIconModerateOrAboveStyle,
  getSelectedPointIconStyle,
  getLabelStyle,
  moderateOrAbovePointIconStyle,
  offlineOrGoodPointIconStyle,
} from './AQILayer.styles';

type AQIFeatureProperties = {
  aqi: number;
  is_observation_recent: boolean;
  is_offline: boolean;
  is_within_coverage_area: boolean;
  name: string;
  purpleair_sensor_index: number;
};

const REFRESH_INTERVAL_SECONDS = 10 * 60; // 10 minutes

const getFeatureCoordinates = (input: {
  mapInstance?: MapRef;
  sourceId: string;
  featureId: number;
}): LatLng | null => {
  const { mapInstance, sourceId, featureId } = input;

  if (!mapInstance) {
    return null;
  }

  const features = mapInstance.querySourceFeatures(sourceId, {
    sourceLayer: AQI_SOURCE_LAYER,
  });

  const feature = features.find((f) => f.id === featureId);

  if (feature?.geometry.type === 'Point') {
    const [lng, lat] = feature.geometry.coordinates;
    return { lng, lat };
  }

  return null;
};

export const resolveAqiPath = (
  geoJsonFeatures: MapboxFeature<AQIFeatureProperties>[],
): string | null => {
  const feature = geoJsonFeatures[0];
  const featureId = feature.id;
  if (featureId === undefined || featureId === null) return null;
  return `/aqi/${featureId}`;
};

export const aqiClickHandler: MapClickHandler<AQIFeatureProperties> = (
  geoJsonFeatures,
) => {
  const path = resolveAqiPath(geoJsonFeatures);
  if (path) history.push(path);
};

export const AQILayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const activeSensorId = useSelectedAQISensorId();
  const { aqiSensor } = useAQISensor({ id: activeSensorId });
  const mapInstance = useMapInstance();
  const { t } = useTranslation();

  usePulseAnimation({
    baseRadius: BASE_PULSE_CIRCLE_RADIUS,
    isActive: Boolean(activeSensorId),
    layerId: [
      AQI_SELECTED_POINT_ICON_LAYER_ID,
      AQI_SELECTED_POINT_ICON_MODERATE_OR_ABOVE_LAYER_ID,
    ],
    map: mapInstance?.getMap(),
    maxZoomLevel: MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
    minZoomLevel: MIN_ICON_ZOOM_LEVEL,
  });

  const activeSensorLatLng = useMemo(() => {
    if (!activeSensorId) return null;
    const latLngFromFeature = getFeatureCoordinates({
      mapInstance,
      sourceId: AQI_SOURCE_ID,
      featureId: activeSensorId,
    });
    if (!latLngFromFeature && aqiSensor?.location) {
      return aqiSensor.location;
    }
    return latLngFromFeature;
  }, [activeSensorId, mapInstance, aqiSensor?.location]);

  useCenterMap({
    latLng: activeSensorLatLng,
    drawerIsOpen: true,
    defaultSnapPointPct: 0.47,
    zoomIn: true,
  });

  return (
    <VisibilityToggledSource
      id={AQI_SOURCE_ID}
      visible={visible}
      url={getWatchdutyTileUrl(AQI_SOURCE_LAYER)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={AQI_BASE_POINT_BLURS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getBasePointBlurStyle(activeSensorId), visible)}
      />
      <Layer
        id={AQI_POINT_BLURS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getPointBlurStyle(activeSensorId), visible)}
      />
      <Layer
        id={AQI_SELECTED_POINT_ICON_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getSelectedPointIconStyle(activeSensorId), visible)}
      />
      <Layer
        id={AQI_SELECTED_POINT_ICON_MODERATE_OR_ABOVE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(
          getSelectedPointIconModerateOrAboveStyle(activeSensorId),
          visible,
        )}
      />
      <Layer
        id={AQI_POINT_ICONS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(offlineOrGoodPointIconStyle, visible)}
      />
      <Layer
        id={AQI_POINT_ICONS_MODERATE_OR_ABOVE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(moderateOrAbovePointIconStyle, visible)}
      />
      <Layer
        id={AQI_LABELS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getLabelStyle(t), visible)}
      />
    </VisibilityToggledSource>
  );
};
